import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface ArticleLayoutProps {
  title: string;
  description: string;
  heroImageSrc: string;
  heroImageAlt: string;
  tocItems: string[];
  breadcrumbName: string;
  canonicalUrl: string;
  articleSchema: Record<string, unknown>;
  readTime: string;
  badge: string;
  children: ReactNode;
}

export default function ArticleLayout({
  title,
  description,
  heroImageSrc,
  heroImageAlt,
  tocItems,
  breadcrumbName,
  canonicalUrl,
  articleSchema,
  readTime,
  badge,
  children,
}: ArticleLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Article Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-database-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
                Hipobuy Sheet
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-200"
              >
                Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                buy it now
                <i className="ri-shopping-bag-3-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200">
                Home
              </Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-700 font-medium">{breadcrumbName}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 mb-4">
            <i className="ri-book-open-line text-accent-600 text-sm"></i>
            <span className="text-xs font-medium text-accent-700">{badge}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
            {title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            {description}
          </p>
          <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
            <span>Updated: July 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{readTime}</span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-10 aspect-[21/9]">
          <img
            src={heroImageSrc}
            alt={heroImageAlt}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Table of Contents */}
        <nav className="bg-background-100 rounded-2xl p-5 md:p-6 mb-10 border border-background-200/70" aria-label="Table of Contents">
          <h2 className="text-sm font-semibold text-foreground-900 mb-3">Table of Contents</h2>
          <ol className="space-y-1.5">
            {tocItems.map((item, idx) => (
              <li key={idx}>
                <a href={`#section-${idx + 1}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200">
                  {idx + 1}. {item}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Article Body */}
        <div className="prose-custom space-y-10 text-foreground-700">
          {children}
        </div>
      </article>

      {/* Article Footer */}
      <footer className="border-t border-background-200 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-500">
              &copy; {new Date().getFullYear()} Hipobuy Spreadsheet Hub. All rights reserved.
            </p>
            <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 transition-colors duration-200">
              ← Return to Homepage
            </Link>
          </div>
        </div>
      </footer>

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://hipobuyspread.space/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: breadcrumbName,
                item: canonicalUrl,
              },
            ],
          }),
        }}
      />
    </div>
  );
}